import { gql, useMutation } from '@apollo/client';
import classNames from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../components/Button';
import Toast from '../../components/Toast';
import { useQuill } from '../../editor/QuillContext';
import type {
  ReactionThreadFull_ThreadFragment,
  ResolveThreadMutation,
  ResolveThreadMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import RESOLVE_THREAD_MUTATION from '../../graphql/resolveThread';
import UIActions from '../../ui/UIActions';
import handleUnresolveThreadError from './handleUnresolveThreadError';
import { useAddReaction, useDeleteReaction } from './reactionsMutations';
import ThreadReactionList from './ThreadReactionList';

const THREAD_FRAGMENT = gql`
  fragment ReactionThreadFull_thread on Thread {
    id
    comments {
      id
    }
    reactions {
      id
      ...ThreadReactionList_reactions
    }
  }
  ${ThreadReactionList.fragments.reactions}
`;

export interface ReactionThreadFullProps {
  thread: ReactionThreadFull_ThreadFragment;
  isExpanded?: boolean;
  unresolveThread: (threadId: string) => Promise<unknown>;
}

const ReactionThreadFull = ({
  thread,
  isExpanded = false,
  unresolveThread,
}: ReactionThreadFullProps) => {
  const dispatch = useDispatch();

  const addReaction = useAddReaction();
  const deleteReaction = useDeleteReaction();

  const { quill } = useQuill();

  const [resolveThreadMutation] = useMutation<
    ResolveThreadMutation,
    ResolveThreadMutationVariables
  >(RESOLVE_THREAD_MUTATION);

  const resolveThread = () =>
    resolveThreadMutation({ variables: { id: thread.id } });

  const handleResolve = () => {
    if (!quill) {
      return;
    }

    resolveThread().then(() => {
      dispatch(
        UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
          <Toast
            level="success"
            onDismiss={toastProps.onDismiss}
            message="Comment resolved"
            secondaryText="Undo"
            onSecondaryClick={() => {
              toastProps.onDismiss();
              unresolveThread(thread.id).catch((e) =>
                handleUnresolveThreadError(e, dispatch),
              );
            }}
          />
        )),
      );
    });
  };

  const handleAddReaction = (emoji: string) => {
    addReaction(emoji, thread.id);
  };

  const handleRemoveReaction = (id: string) => {
    deleteReaction(id, thread, quill);
  };

  const { reactions } = thread;

  return (
    <div
      className={classNames('reaction-thread-full', {
        expanded: isExpanded,
      })}
    >
      {isExpanded && (
        <div className="resolve-button-container">
          <Button variant="link" size="micro" onClick={handleResolve}>
            Resolve
          </Button>
        </div>
      )}
      <div className="reaction-thread-reactions">
        <ThreadReactionList
          readOnly={!isExpanded}
          reactions={reactions}
          onAdd={handleAddReaction}
          onRemove={handleRemoveReaction}
        />
      </div>
    </div>
  );
};

ReactionThreadFull.fragments = {
  thread: THREAD_FRAGMENT,
};

export default ReactionThreadFull;
